import { countryForSticker } from './albumDecorations';

/* ── Color tokens for the sticker-album page ─────────────────────────── */
export const C = {
  primary:            '#10B981',
  primaryDim:         '#065f46',
  primaryContainer:   '#69f6b8',
  onPrimaryContainer: '#064e3b',
  onPrimaryFixedVar:  '#006544',
  secondary:          '#0057bd',
  secondaryContainer: '#bfd1ff',
  tertiary:           '#815100',
  tertiaryContainer:  '#f8a010',
  error:              '#b31b25',
  surface:            '#d8fff0',
  surfaceDim:         '#8fe5c9',
  surfaceContainer:   '#b2f6de',
  surfaceContainerLow:'#bffee7',
  surfaceContainerHigh:'#a7f1d8',
  surfaceContainerHighest:'#9decd2',
  onSurface:          '#00362a',
  onSurfaceVariant:   '#2f6555',
  outline:            '#4c8170',
  outlineVariant:     '#81b8a5',
  /* Reservas activas — verde apagado: la figurita es "casi tuya". */
  reserved:           '#10B98180',  // primary @ 50%
  reservedRing:       '#065f46',    // primaryDim para anillos y badge
  reservedSoft:       '#10B98122',  // tinte para fondos
  /* "Other user" álbum — teal-emerald (mismo eje HSL que primary). */
  otherUser:          '#0D9488',
  otherUserDim:       '#134E4A',
  otherUserSoft:      '#0D948822',
};

/* ── View mode color palettes ────────────────────────────────────────── */
export const VIEW_COLORS = {
  MY_ALBUM: {
    owned:    { bg: C.primary, text: '#ffffff', border: 'transparent' },
    missing:  { bg: 'transparent', text: C.primary, border: C.primary },
    selected: { outline: C.onPrimaryContainer, shadow: `${C.onPrimaryContainer}66` },
    badge:    C.onPrimaryContainer,
  },
  USER_ALBUM: {
    owned:    { bg: C.otherUser, text: '#ffffff', border: 'transparent' },
    missing:  { bg: 'transparent', text: C.otherUser, border: C.otherUser },
    selected: { outline: C.otherUserDim, shadow: `${C.otherUserDim}66` },
    badge:    C.otherUserDim,
  },
  TRADE_MATCH: {
    /* "te dan" (ellos tienen, yo necesito) → amber */
    theyHaveYouNeed: { bg: '#F59E0B', text: '#ffffff', border: 'transparent' },
    /* "les das" (yo tengo, ellos necesitan) → primary verde */
    youHaveTheyNeed: { bg: C.primary, text: '#ffffff', border: 'transparent' },
    bothHave:        { bg: '#94A3B8', text: '#ffffff', border: 'transparent' },
    neitherHas:      { bg: 'transparent', text: '#CBD5E1', border: '#CBD5E1' },
    selected:        { outline: '#1e293b', shadow: '#1e293b66' },
  },
  TRADE_REVIEW: {
    youReceive:  { bg: '#16a34a', text: '#ffffff', border: 'transparent' },   // verde, lo que recibo
    youGive:     { bg: '#EF4444', text: '#ffffff', border: 'transparent' },   // rojo, lo que doy
    // Las que no participan en el trade quedan neutrales — sin highlight —
    // para que solo se destaquen las del intercambio.
    yourOwned:   { bg: 'transparent', text: '#9ca3af', border: '#e5e7eb' },
    yourMissing: { bg: 'transparent', text: '#cbd5e1', border: '#e5e7eb' },
  },
};

/* ── Trade palette resolver ─────────────────────────────────────────── */
export function getTradePalette(theme = null) {
  return {
    receive: {
      bg:     theme?.tradeReceiveBg     ?? VIEW_COLORS.TRADE_MATCH.theyHaveYouNeed.bg,
      text:   theme?.tradeReceiveText   ?? VIEW_COLORS.TRADE_MATCH.theyHaveYouNeed.text,
      strong: theme?.tradeReceiveStrong ?? '#B45309',
    },
    give: {
      bg:     theme?.tradeGiveBg     ?? VIEW_COLORS.TRADE_MATCH.youHaveTheyNeed.bg,
      text:   theme?.tradeGiveText   ?? VIEW_COLORS.TRADE_MATCH.youHaveTheyNeed.text,
      strong: theme?.tradeGiveStrong ?? C.primaryDim,
    },
    both: {
      bg:     theme?.tradeBothBg     ?? VIEW_COLORS.TRADE_MATCH.bothHave.bg,
      text:   theme?.tradeBothText   ?? VIEW_COLORS.TRADE_MATCH.bothHave.text,
      strong: theme?.tradeBothStrong ?? '#475569',
    },
    neither: {
      bg:     theme?.tradeNeitherBg     ?? VIEW_COLORS.TRADE_MATCH.neitherHas.bg,
      text:   theme?.tradeNeitherText   ?? VIEW_COLORS.TRADE_MATCH.neitherHas.text,
      border: theme?.tradeNeitherBorder ?? VIEW_COLORS.TRADE_MATCH.neitherHas.border,
    },
    chrome: {
      headerBg:           theme?.tradeHeaderBg ?? null,
      headerText:         theme?.tradeHeaderText ?? null,
      headerBorder:       theme?.tradeHeaderBorder ?? null,
      reviewHeaderBg:     theme?.tradeReviewHeaderBg ?? null,
      reviewHeaderText:   theme?.tradeReviewHeaderText ?? null,
      reviewHeaderBorder: theme?.tradeReviewHeaderBorder ?? null,
      summaryBg:          theme?.tradeSummaryBg ?? null,
      summaryText:        theme?.tradeSummaryText ?? null,
    },
    ownedDim: {
      bg:   theme?.tradeOwnedDimBg ?? null,
      text: theme?.tradeOwnedDimText ?? null,
    },
    selected: {
      outline: theme?.tradeSelectedOutline ?? VIEW_COLORS.TRADE_MATCH.selected.outline,
      shadow:  theme?.tradeSelectedShadow ?? VIEW_COLORS.TRADE_MATCH.selected.shadow,
    },
  };
}

/* ── Base sticker cell style ─────────────────────────────────────────── */
const stickerBase = {
  aspectRatio: '1 / 1',
  fontSize: 11,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  position: 'relative',
};

/* ── Style resolver: returns inline style object for a sticker cell ─── */
// theme: optional override object (see albumVariants.js). Only MY_ALBUM-mode colors are themable.
export function resolveStickerStyle(sticker, viewMode, selected, { myOwnedSet, theirOwnedSet, mySpareSet, theirSpareSet, myCounts, activeTrade, tradeBuilder, lockedSet, incomingSet } = {}, theme = null) {
  const isSelected = selected.has(sticker.n);
  const lockedCount = lockedSet?.get?.(sticker.n) ?? 0;
  const incomingCount = incomingSet?.get?.(sticker.n)?.length ?? 0;
  // Variante "Country Colors": cada figurita se pinta con el color de su país.
  const countryColor = theme?.useCountryColors ? countryForSticker(sticker.n)?.color : null;

  let base;
  // En MY_ALBUM las "repetidas netas" descuentan los locks: si tengo 2 pero 1 está
  // reservada, no quiero verla como repetida.
  const effectiveReps = Math.max(0, (sticker.reps ?? 0) - (sticker.owned ? lockedCount : 0));
  let badgeValue = effectiveReps >= 2 ? effectiveReps : null;

  switch (viewMode) {
    case 'USER_ALBUM': {
      const colors = sticker.owned ? VIEW_COLORS.USER_ALBUM.owned : VIEW_COLORS.USER_ALBUM.missing;
      const ownedBg     = countryColor ?? colors.bg;
      const ownedText   = countryColor ? '#ffffff' : colors.text;
      const missingText = countryColor ?? colors.text;
      const missingBorder = countryColor ?? colors.border;
      base = {
        ...stickerBase,
        backgroundColor: sticker.owned ? ownedBg : 'transparent',
        color: sticker.owned ? ownedText : missingText,
        border: sticker.owned ? '1px solid transparent' : `1px dashed ${missingBorder}`,
      };
      break;
    }

    case 'TRADE_MATCH': {
      const palette = getTradePalette(theme);
      const iOwn = myOwnedSet?.has(sticker.n);
      const theyOwn = theirOwnedSet?.has(sticker.n);
      // Solo se highlightea como "te dan / les das" si hay realmente
      // figuritas swappables (count >= 2 del otro lado, count == 0 en el
      // mío o viceversa).
      const iSpare = mySpareSet?.has(sticker.n);
      const theySpare = theirSpareSet?.has(sticker.n);

      // Las celdas no accionables (both, neither, "tengo 1 sin spare", "tienen
      // 1 sin spare") son ruido — quedan casi invisibles. La leyenda del header
      // sólo muestra verde y rojo; mostrar grises mezclaba info no clickeable
      // con la accionable y confundía al user.
      // Si ya voy a recibir la figurita en otro trade ACCEPTED, tampoco la
      // mostramos como "te pueden dar" — ya la estoy recibiendo, no necesito
      // pedirla acá. (incomingCount = number of accepted trades that bring it.)
      const isReceive = theySpare && !iOwn && incomingCount === 0;
      const isGive    = iSpare && !theyOwn;
      const isActionable = isReceive || isGive;

      if (!isActionable) {
        base = {
          ...stickerBase,
          backgroundColor: 'transparent',
          color: 'transparent',
          border: '1px dashed transparent',
        };
        badgeValue = null;
        // Salimos del case para evitar pintar contadores / outlines de trade
        // builder en celdas que el user ni siquiera ve.
        break;
      }

      const colors = isReceive ? palette.receive : palette.give;
      base = {
        ...stickerBase,
        backgroundColor: colors.bg,
        color: colors.text,
        border: '1px solid transparent',
      };

      // Trade builder: marcar las que están en offering / requesting con outline.
      // Coherente con la paleta: requested = amber (lo que vamos a recibir),
      // offered = primaryDim verde (lo que vamos a dar — mío y "se va").
      const inOffering = tradeBuilder?.offering?.has(sticker.n);
      const inRequesting = tradeBuilder?.requesting?.has(sticker.n);
      if (inRequesting) {
        base = {
          ...base,
          outline: `3px solid ${palette.receive.strong}`,
          outlineOffset: -2,
          boxShadow: `0 0 6px ${palette.receive.strong}66`,
          zIndex: 5,
        };
      } else if (inOffering) {
        base = {
          ...base,
          outline: `3px solid ${palette.give.strong}`,
          outlineOffset: -2,
          boxShadow: `0 0 6px ${palette.give.strong}66`,
          zIndex: 5,
        };
      }

      // En TRADE_MATCH viendo el álbum del otro, sobre las celdas "les podés
      // dar" (mías repe que el otro no tiene) mostramos el contador de repes
      // libres mías para que el user vea de cuál tiene más stock al elegir
      // qué ofrecer. Para "te pueden dar" no mostramos nada — son del otro,
      // no nos sirve saber su stock.
      if (iSpare && !theyOwn && myCounts) {
        const spare = Math.max(0, (myCounts[sticker.n - 1] || 0) - 1);
        badgeValue = spare >= 1 ? spare : null;
      } else {
        badgeValue = null;
      }
      break;
    }

    case 'TRADE_REVIEW':
    case 'TRADE_DETAIL':
    case 'OFFER_REVIEW': {
      // youGive/youReceive ya están desde MI perspectiva (mappedIncoming/Outgoing
      // los pre-calculan). Si por compat falta el campo, caemos al esquema viejo.
      const giveSet  = activeTrade?.youGive    ?? activeTrade?.stickersOffered  ?? [];
      const recvSet  = activeTrade?.youReceive ?? activeTrade?.stickersRequested ?? [];
      const palette = getTradePalette(theme);
      let colors;
      if (recvSet.includes(sticker.n)) {
        colors = {
          bg: palette.receive.bg,
          text: palette.receive.text,
          border: 'transparent',
        };
      } else if (giveSet.includes(sticker.n)) {
        colors = {
          bg: palette.give.bg,
          text: palette.give.text,
          border: 'transparent',
        };
      }
      else if (myOwnedSet?.has(sticker.n)) {
        colors = {
          bg: palette.ownedDim.bg ?? VIEW_COLORS.TRADE_REVIEW.yourOwned.bg,
          text: palette.ownedDim.text ?? VIEW_COLORS.TRADE_REVIEW.yourOwned.text,
          border: VIEW_COLORS.TRADE_REVIEW.yourOwned.border,
        };
      } else {
        colors = {
          bg: palette.neither.bg,
          text: palette.neither.text ?? VIEW_COLORS.TRADE_REVIEW.yourMissing.text,
          border: palette.neither.border ?? VIEW_COLORS.TRADE_REVIEW.yourMissing.border,
        };
      }

      const isHighlighted = recvSet.includes(sticker.n) || giveSet.includes(sticker.n);
      base = {
        ...stickerBase,
        backgroundColor: colors.bg,
        color: colors.text,
        border: isHighlighted ? '1px solid transparent' : `1px dashed ${colors.border}`,
        opacity: isHighlighted ? 1 : 0.55,
      };
      badgeValue = null;
      break;
    }

    case 'OFFER_BUILDER': {
      // Pinta como MY_ALBUM (mías vs faltantes) y marca en rojo lo elegido
      // como "doy" y verde lo elegido como "pido". Cells con count == 1 no
      // son utilizables: las dejamos atenuadas.
      const ownedBg     = theme?.stickerOwnedBg     ?? VIEW_COLORS.MY_ALBUM.owned.bg;
      const ownedText   = theme?.stickerOwnedText   ?? VIEW_COLORS.MY_ALBUM.owned.text;
      const missingText = theme?.stickerMissingText ?? VIEW_COLORS.MY_ALBUM.missing.text;
      const missingBorder = theme?.stickerMissingBorder ?? VIEW_COLORS.MY_ALBUM.missing.border;
      const myFree = (sticker.reps ?? 0) - (sticker.owned ? lockedCount : 0);
      const giveable = myFree >= 2;
      const wantable = !sticker.owned;
      base = {
        ...stickerBase,
        backgroundColor: sticker.owned ? ownedBg : 'transparent',
        color: sticker.owned ? ownedText : missingText,
        border: sticker.owned ? '1px solid transparent' : `1px dashed ${missingBorder}`,
        opacity: (giveable || wantable) ? 1 : 0.45,
      };
      const inOffering   = tradeBuilder?.offering?.has(sticker.n);
      const inRequesting = tradeBuilder?.requesting?.has(sticker.n);
      if (inOffering) {
        base = { ...base, backgroundColor: '#EF4444', color: '#ffffff', border: '1px solid transparent', outline: '3px solid #B91C1C', outlineOffset: -2, boxShadow: '0 0 6px #EF444466', zIndex: 5, opacity: 1 };
      } else if (inRequesting) {
        base = { ...base, backgroundColor: '#16a34a', color: '#ffffff', border: '1px solid transparent', outline: '3px solid #166534', outlineOffset: -2, boxShadow: '0 0 6px #16a34a66', zIndex: 5, opacity: 1 };
      }
      badgeValue = null;
      break;
    }

    // MY_ALBUM (default)
    default: {
      const ownedBg     = countryColor ?? theme?.stickerOwnedBg     ?? VIEW_COLORS.MY_ALBUM.owned.bg;
      const ownedText   = theme?.stickerOwnedText   ?? VIEW_COLORS.MY_ALBUM.owned.text;
      const missingText = countryColor ?? theme?.stickerMissingText ?? VIEW_COLORS.MY_ALBUM.missing.text;
      const missingBorder = countryColor ?? theme?.stickerMissingBorder ?? VIEW_COLORS.MY_ALBUM.missing.border;
      base = {
        ...stickerBase,
        backgroundColor: sticker.owned ? ownedBg : 'transparent',
        color: sticker.owned ? ownedText : missingText,
        border: sticker.owned ? '1px solid transparent' : `1px dashed ${missingBorder}`,
      };
      // "Owned but locked" → verde apagado, como si fuera tuya, pero casi.
      if (sticker.owned && lockedCount > 0) {
        base = {
          ...base,
          opacity: 0.55,
          outline: `1.5px solid ${C.reservedRing}`,
          outlineOffset: -1,
        };
      }
      // "Incoming" → todavía no la tengo, pero la voy a recibir en un trade
      // ACCEPTED. La pintamos como "ghost owned": fondo de owned pero con
      // opacidad reducida y borde sólido (no dashed) para diferenciarla de
      // las que faltan de verdad. Si ya la tengo no aplica.
      if (!sticker.owned && incomingCount > 0) {
        base = {
          ...stickerBase,
          backgroundColor: ownedBg,
          color: ownedText,
          border: '1px solid transparent',
          opacity: 0.45,
        };
      }
      break;
    }
  }

  // Selection ring
  if (isSelected) {
    let outline;
    let shadow;
    if (viewMode === 'TRADE_MATCH' || viewMode === 'TRADE_REVIEW' || viewMode === 'TRADE_DETAIL' || viewMode === 'OFFER_REVIEW') {
      const palette = getTradePalette(theme);
      outline = palette.selected.outline;
      shadow = palette.selected.shadow;
    } else {
      const selColors = VIEW_COLORS[viewMode]?.selected || VIEW_COLORS.MY_ALBUM.selected;
      outline = (viewMode === 'MY_ALBUM' && theme?.stickerSelectedOutline) ?? selColors.outline;
      shadow = (viewMode === 'MY_ALBUM' && theme?.stickerSelectedShadow) ?? selColors.shadow;
    }
    base = {
      ...base,
      outline: `3px solid ${outline}`,
      outlineOffset: -2,
      boxShadow: `0 0 6px ${shadow}`,
      zIndex: 5,
    };
  }

  return { style: base, badgeValue };
}

/* ── Badge style ─────────────────────────────────────────────────────── */
export function getBadgeStyle(viewMode, theme = null) {
  const color = (viewMode === 'MY_ALBUM' && theme?.stickerBadgeBg)
    || VIEW_COLORS[viewMode]?.badge
    || VIEW_COLORS.MY_ALBUM.badge;
  return {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 15,
    height: 15,
    backgroundColor: color,
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    fontWeight: 800,
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
    zIndex: 10,
  };
}
